import { Base } from '@studiometa/js-toolkit';
import { toggleClass, addClass, removeClass, nextFrame } from '@studiometa/js-toolkit/utils';

/**
 * Header component
 */
export default class Header extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'Header',
    refs: [
      'mainNav',
      'searchForm',
      'searchInput',
      'searchButton',
      'searchPanel',
      'searchPanelButton',
      'burgerButton',
      'burgerMenu',
      'burgerSection[]',
      'menuMobile',
    ],
  };

  sticky = false;

  inverted = false;

  visible = false;

  lastScrollY = 0;

  burgerVisible = false;

  searchPanelVisible = false;

  rootElement = undefined;

  /**
   * Mounted
   */
  mounted() {
    this.rootElement = document.querySelector(':root');

    this.height = this.$el.clientHeight;
    this.top =
      this.$el.getBoundingClientRect().top +
      window.scrollY -
      (Number.parseInt(
        getComputedStyle(document.body).getPropertyValue('--ginToolbarHeight'),
        10,
      ) || 0);
    this.timing = 200;
    this.animTimeout = null;
  }

  /**
   * Scrolled
   */
  scrolled() {
    let shouldRender = false;

    if (!this.sticky && window.scrollY > this.top + this.height) {
      this.sticky = true;
      this.inverted = true;

      clearTimeout(this.animTimeout);
      nextFrame(() => {
        this.$el.classList.add('header--animated');
      });

      shouldRender = true;
    }

    if (this.sticky && window.scrollY < this.top) {
      this.sticky = false;
      this.inverted = false;

      clearTimeout(this.animTimeout);
      this.animTimeout = setTimeout(() => {
        this.$el.classList.remove('header--animated');
      }, this.timing);

      shouldRender = true;
    }

    if (!this.visible && window.scrollY < this.lastScrollY && this.sticky) {
      this.visible = true;
      shouldRender = true;
    }

    if (
      this.visible &&
      window.scrollY > this.lastScrollY &&
      this.sticky &&
      window.scrollY > this.top + this.height
    ) {
      this.visible = false;
      shouldRender = true;
    }

    if (shouldRender) {
      toggleClass(this.$el, 'header--sticky', this.sticky);
      toggleClass(this.$refs.burgerMenu, 'burger-menu--sticky', this.sticky);
      toggleClass(this.$el, 'header--inverted', this.inverted);
      toggleClass(this.$el, 'header--visible', this.visible);

      if (this.sticky && this.visible) {
        this.rootElement.style.setProperty('--top-header', `${this.$el.clientHeight}px`);
      } else {
        this.rootElement.style.setProperty('--top-header', `0px`);
      }
    }

    this.lastScrollY = window.scrollY;
  }

  /**
   * Show burger menu
   */
  showBurgerMenu() {
    addClass(this.$refs.searchForm, 'burger-menu__search-form--visible');
    addClass(this.$refs.burgerMenu, 'burger-menu--visible');
    addClass(this.$refs.mainNav, 'opacity-0');
    addClass(document.body, 'overflow-hidden');
    addClass(this.$el, 'header--opened');
    removeClass(this.$refs.burgerSection, 'burger-menu__menu--hidden');
    removeClass(this.$refs.menuMobile, 'hidden');
    removeClass(this.$refs.searchPanel, 'burger-menu__search-panel--visible');
    this.searchPanelVisible = false;
    this.burgerVisible = true;
  }

  /**
   * Hide burger menu
   */
  hideBurgerMenu() {
    this.hideSearchPanel();
    removeClass(this.$refs.searchForm, 'burger-menu__search-form--visible');
    removeClass(this.$refs.burgerMenu, 'burger-menu--visible');
    removeClass(this.$refs.mainNav, 'opacity-0');
    removeClass(document.body, 'overflow-hidden');
    removeClass(this.$el, 'header--opened');
    this.burgerVisible = false;
  }

  /**
   * Show search panel
   */
  showSearchPanel() {
    this.showBurgerMenu();
    addClass(this.$refs.burgerSection, 'burger-menu__menu--hidden');
    addClass(this.$refs.menuMobile, 'hidden');
    addClass(this.$refs.burgerSection, 'hidden');
    removeClass(this.$refs.searchPanel, 'hidden');
    addClass(this.$refs.searchForm, 'burger-menu__search-form--active');
    addClass(this.$refs.searchPanel, 'burger-menu__search-panel--visible');
    addClass(this.$refs.searchPanelButton, 'burger-menu__search-panel-button--active');
    this.$refs.searchPanel.removeAttribute('aria-hidden');
    this.searchPanelVisible = true;
  }

  /**
   * Hide search panel
   */
  hideSearchPanel() {
    removeClass(this.$refs.burgerSection, 'hidden');
    removeClass(this.$refs.burgerSection, 'burger-menu__menu--hidden');
    removeClass(this.$refs.menuMobile, 'hidden');
    removeClass(this.$refs.searchForm, 'burger-menu__search-form--active');
    removeClass(this.$refs.searchPanel, 'burger-menu__search-panel--visible');
    removeClass(this.$refs.searchPanelButton, 'burger-menu__search-panel-button--active');
    addClass(this.$refs.searchPanel, 'hidden');
    this.$refs.searchPanel.setAttribute('aria-hidden', 'true');
    this.searchPanelVisible = false;
  }

  /**
   * Toggle burgerMenu on burgerButton click
   * @returns {Function}
   */
  onBurgerButtonClick() {
    return this.burgerVisible || this.searchPanelVisible
      ? this.hideBurgerMenu()
      : this.showBurgerMenu();
  }

  /**
   * Focus searchInput on searchPanelButton click
   * @returns {Function}
   */
  onSearchPanelButtonClick() {
    return this.searchPanelVisible ? this.hideSearchPanel() : this.$refs.searchInput.focus();
  }

  /**
   * Show searchPanel on searchInput focus
   */
  onSearchInputFocusin() {
    this.showSearchPanel();
  }

  /**
   * Toggle disabled on searchButton
   */
  onSearchInputInput() {
    if (this.$refs.searchInput.value.length <= 0) {
      this.$refs.searchButton.setAttribute('disabled', 'disabled');
    } else {
      this.$refs.searchButton.removeAttribute('disabled');
    }
  }
}
